<script>
import { format } from 'date-fns/fp'
import EventsCalendarMixin from '@events/mixins/events-calendar-mixin'

export default {
  name: 'EventBigCalendar',
  mixins: [EventsCalendarMixin],
  props: {
    events: {
      type: Array,
      required: true,
    },
    currentDate: {
      type: Date,
      required: true,
    },
  },
  data() {
    return {
      expanded: false,
      weekIndex: 0,
    }
  },
  computed: {
    formattedMonth() {
      return format('MMMM Y', this.currentDateNow())
    },
  },
  methods: {
    currentDateNow() {
      return this.currentDate
    },
    expand() {
      this.expanded = !this.expanded
    },
    nextWeek() {
      this.weekIndex += 1
      if (this.weekIndex >= this.weeks.length) {
        this.nextWithoutEmit()
        // now the weekIndex is set to 0
        // if the last week contains days from different months
        // it means that we've already seen part of the week as part of the last week of the month being displayed
        const week = this.weeks[0]
        const lastDay = week.days[week.days.length - 1]
        const firstDay = week.days[0]
        if (firstDay.date.getMonth() !== lastDay.date.getMonth()) {
          this.weekIndex++
        }
      }

      const week = this.weeks[this.weekIndex].days
      const fromDate = week[0]
      const toDate = week[week.length - 1]
      this.filterEvents(fromDate, toDate)
    },
    previousWeek() {
      this.weekIndex -= 1
      // we were at the firs week of the month
      // this means we move to the previous month
      if (this.weekIndex < 0) {
        this.previousWithoutEmit()
        // select the last week from the previous month
        this.weekIndex = this.weeks.length - 1
        // if the last week contains days from different months
        // it means that we've already seen part of the week as part of the first week of the month being displayed
        const week = this.weeks[this.weeks.length - 1]
        const lastDay = week.days[week.days.length - 1]
        const firstDay = week.days[0]
        if (firstDay.date.getMonth() !== lastDay.date.getMonth()) {
          this.weekIndex--
        }
      }
      // get the dates
      const week = this.weeks[this.weekIndex].days
      const fromDate = week[0]
      const toDate = week[week.length - 1]
      this.filterEvents(fromDate, toDate)
    },
  },
}
</script>

<template>
  <div class="flex shadow-box rounded-xl bg-blue-lighter">
    <div class="events-calendar">
      <div class="flex justify-between">
        <div class="text-blue-darker text-lg">
          {{ formattedMonth }}
        </div>
        <div class="align-middle">
          <button
            class="inline-block align-middle focus:outline-none"
            @click="previous"
            ><svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="18"
              viewBox="0 0 9.677 18"
            >
              <g
                id="Group_85"
                data-name="Group 85"
                transform="translate(10) rotate(90)"
              >
                <path
                  id="Path_37"
                  data-name="Path 37"
                  d="M8.919,9.677,0,.757.757,0,8.919,8.162,17.081,0l.758.757Z"
                  fill="#069bd7"
                />
              </g></svg
          ></button>
          <button
            class="inline-block align-middle focus:outline-none ml-4"
            @click="next"
            ><svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="18"
              viewBox="0 0 9.677 18"
            >
              <g
                id="Group_84"
                data-name="Group 84"
                transform="translate(0 18) rotate(-90)"
              >
                <path
                  id="Path_37"
                  data-name="Path 37"
                  d="M8.919,9.677,0,.757.757,0,8.919,8.162,17.081,0l.758.757Z"
                  fill="#069bd7"
                />
              </g></svg
          ></button>
        </div>
        <button
          class="select inverted w-38 text-sm rounded-small-button px-3 text-left"
          @click="expand"
        >
          Expand Full Month
        </button>
      </div>
      <div v-show="!expanded" class="week-days">
        <button
          v-for="day in weeks[weekIndex].days"
          :key="day.date.valueOf()"
          class="focus:outline-none"
          :class="[
            day.isCurrentMonth ? 'current-month' : 'other-month',
            day.hasEvents ? 'has-events' : '',
          ]"
          @click="filterEvents(day)"
          >{{ day.number }}<span v-show="day.hasEvents" class="bullet"></span
        ></button>
      </div>
      <div v-show="expanded" class="block mb-3">
        <!-- this key is not ideal -->
        <div
          v-for="(days, index) in weeks"
          :key="days.days[0].date.valueOf()"
          class="week-days"
          :class="[index >= weeks.length - 1 ? 'pb-1' : '']"
        >
          <button
            v-for="day in days.days"
            :key="day.date.valueOf()"
            class="focus:outline-none"
            :class="[
              day.isCurrentMonth ? 'current-month' : 'other-month',
              day.hasEvents ? 'has-events' : '',
            ]"
            @click="filterEvents(day)"
            >{{ day.number }}<span v-show="day.hasEvents" class="bullet"></span
          ></button>
        </div>
      </div>
    </div>
    <div class="rounded-r-xl w-2/5 px-6 py-6 flex flex-col justify-end">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1000 957.8"
        class="mb-2"
        fill="#069BD7"
        width="34"
        height="32"
      >
        <title>Kyndhub_Events_Icon</title>
        <path
          d="M975.44,151.77H783.87V46.68a24.56,24.56,0,0,0-49.08,0V151.82H265.21V46.68a24.56,24.56,0,0,0-49.08-2q0,1,0,2V151.82H24.56A24.56,24.56,0,0,0,0,176.34v778A24.56,24.56,0,0,0,24.56,978.9H975.44A24.56,24.56,0,0,0,1000,954.34h0v-778a24.57,24.57,0,0,0-24.56-24.57Zm-24.56,778H49.08V460.2h901.8Zm0-518.66H49.08V200.9h167v61.87a24.56,24.56,0,0,0,49.08,0V200.9H734.74v61.87a24.57,24.57,0,0,0,49.09,0V200.9h167Z"
          transform="translate(0 -21.1)"
        />
      </svg>
      <div class="flex">
        <div class="mr-4">
          <button
            class="text-lg text-blue-darker block focus:outline-none"
            @click="previousWeek"
            >&lt; Back</button
          >
          <span class="text-sm text-blue-darker block">Previous</span>
        </div>
        <div>
          <button
            class="text-lg text-blue-darker block focus:outline-none"
            @click="nextWeek"
            >Next &gt;</button
          >
          <span class="text-sm text-blue-darker block">Upcoming</span>
        </div>
      </div>
    </div>
  </div>
</template>
